'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';

import { createMediaFilter } from '@/actions/media';

type UseCreateMediaFilterParams = {
  imageUrl: string | undefined;
  mediaId: number | undefined;
  filterName: string;
  enabled?: boolean;
};

type CreatedFilter = {
  key: string;
  url: string | undefined;
};

// Media filters requested since the last full page load (client-side navigation keeps
// these maps). Carousel clones (react-slick `infinite`), the grid and remounts render
// the same media several times; one request is enough, and a failed one is not retried.
const pendingFilterRequests = new Map<string, Promise<string | undefined>>();
// Settled requests: the created url, or undefined when creation failed.
const settledFilterUrls = new Map<string, string | undefined>();

// The cache only changes alongside a state update of the hook that requested
// the filter, so there is nothing to subscribe to.
const subscribeToNothing = () => () => {};

function filterKey(mediaId: number | undefined, filterName: string) {
  return `${mediaId}:${filterName}`;
}

function requestMediaFilter(mediaId: number, filterName: string) {
  const key = filterKey(mediaId, filterName);
  let request = pendingFilterRequests.get(key);

  if (!request) {
    request = createMediaFilter(mediaId, filterName)
      .then((response) =>
        'url' in response && response.url ? response.url : undefined,
      )
      .catch(() => undefined)
      .then((url) => {
        pendingFilterRequests.delete(key);
        settledFilterUrls.set(key, url);
        return url;
      });
    pendingFilterRequests.set(key, request);
  }

  return request;
}

export function useCreateMediaFilter({
  imageUrl,
  mediaId,
  filterName,
  enabled = true,
}: UseCreateMediaFilterParams) {
  const [created, setCreated] = useState<CreatedFilter | null>(null);
  const key = filterKey(mediaId, filterName);
  // Read through useSyncExternalStore so hydration starts from the server value
  // (nothing settled) instead of mismatching when another image filled the cache.
  // undefined = not settled, null = creation failed, string = created url.
  const settledUrl = useSyncExternalStore(
    subscribeToNothing,
    () =>
      settledFilterUrls.has(key)
        ? (settledFilterUrls.get(key) ?? null)
        : undefined,
    () => undefined,
  );

  useEffect(() => {
    if (
      !enabled ||
      imageUrl !== undefined ||
      mediaId === undefined ||
      settledFilterUrls.has(filterKey(mediaId, filterName))
    ) {
      return;
    }

    let cancelled = false;

    void requestMediaFilter(mediaId, filterName).then((url) => {
      if (!cancelled) setCreated({ key: filterKey(mediaId, filterName), url });
    });

    return () => {
      cancelled = true;
    };
  }, [imageUrl, mediaId, filterName, enabled]);

  // A result for another media or filter (props changed) does not count.
  const result: CreatedFilter | null =
    settledUrl !== undefined
      ? { key, url: settledUrl ?? undefined }
      : created?.key === key
        ? created
        : null;

  return {
    createdUrl: result?.url,
    failed: result !== null && result.url === undefined,
  };
}
